import React from "react";
import PropTypes from "prop-types";
import { Search, Tag } from "lucide-react";

import Button from "./ui/Button";
import { navigateTo } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";

// semantic forces search by meaning; otherwise the archive picks title or meaning itself.
function buildArchiveSearchHref(query, semantic = true) {
  const params = new URLSearchParams({ q: query });
  if (semantic) params.set("mode", "semantic");
  return `/archive?${params.toString()}`;
}

const ArchiveTasteSearchForm = ({
  id,
  prompts = [],
  placeholder = "Try “tense spy drama”",
  defaultValue = "",
  semantic = true,
  submitLabel = "Search",
  surface,
  compact = false,
  promptsClassName = "flex flex-wrap gap-2",
}) => {
  const handleSubmit = (event) => {
    const form = event.currentTarget;
    const formData = new FormData(form);
    const query = String(formData.get("q") || "").trim();

    if (!query) {
      event.preventDefault();
      trackEvent("homepage_archive_browse_submit", { surface });
      navigateTo("/archive");
      return;
    }

    event.preventDefault();
    trackEvent("homepage_archive_search_submit", { surface });
    navigateTo(buildArchiveSearchHref(query, semantic));
  };

  const handlePromptClick = (prompt) => {
    trackEvent("homepage_archive_prompt_click", {
      surface,
      query: prompt,
    });
    navigateTo(buildArchiveSearchHref(prompt, semantic));
  };

  return (
    <div className="space-y-3">
      <form action="/archive" method="GET" className={`archive-taste-search-form ${compact ? "grid gap-2" : "grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]"}`} onSubmit={handleSubmit}>
        <div className="relative">
          <label htmlFor={id} className="sr-only">
            {semantic ? "Search the archive by mood" : "Search the archive"}
          </label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-secondary-readable" aria-hidden="true" />
          <input
            id={id}
            name="q"
            type="text"
            inputMode="search"
            autoComplete="off"
            placeholder={placeholder}
            defaultValue={defaultValue}
          className="archive-taste-search-input h-12 w-full rounded-lg border border-accent-gold/60 bg-background/85 pl-11 pr-4 text-body text-foreground outline-hidden transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/25"
          />
          {semantic ? <input type="hidden" name="mode" value="semantic" /> : null}
        </div>

        {/* As tall as the text box beside it. */}
        <Button type="submit" variant="default" className="archive-taste-search-submit h-12">
          <Search className="h-4 w-4" aria-hidden="true" />
          {submitLabel}
        </Button>
      </form>

      {prompts.length > 0 && (
        <div className={`archive-taste-search-prompts ${promptsClassName}`} aria-label="Suggested archive searches">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => handlePromptClick(prompt)}
              className="inline-flex min-h-9 items-center gap-2 rounded-md border border-accent-gold/55 bg-background/35 px-3 py-1.5 text-body-small text-foreground/75 transition hover:border-secondary/40 hover:bg-secondary/10 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Tag className="h-3.5 w-3.5" aria-hidden="true" />
              {prompt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ArchiveTasteSearchForm.propTypes = {
  id: PropTypes.string.isRequired,
  prompts: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  semantic: PropTypes.bool,
  submitLabel: PropTypes.string,
  surface: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  promptsClassName: PropTypes.string,
};

export { buildArchiveSearchHref };
export default ArchiveTasteSearchForm;
